import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FirebaseRuntimeConfig } from './firebase-config.model';
import { environment } from 'environments/environment';

declare const ngDevMode: boolean | undefined;

@Injectable({ providedIn: 'root' })
export class FirebaseAppService {
  private firebaseApp?: FirebaseApp;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  async getFirebaseApp(): Promise<FirebaseApp | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    if (this.firebaseApp) {
      return this.firebaseApp;
    }

    const existingApps = getApps();
    if (existingApps.length > 0) {
      this.firebaseApp = existingApps[0];
      await this.ensureAuthInitialized(this.firebaseApp);
      return this.firebaseApp;
    }

    const config = this.getConfig();
    if (!config) {
      return null;
    }

    try {
      this.firebaseApp = initializeApp(config.options);
      await this.ensureAuthInitialized(this.firebaseApp);
      return this.firebaseApp;
    } catch (error) {
      console.error('[Firebase] No se pudo inicializar la aplicacion.', error);
      return null;
    }
  }

  getConfig(): FirebaseRuntimeConfig | null {
    const firebaseEnv = environment.firebase;
    
    if (!firebaseEnv.apiKey || !firebaseEnv.appId || !firebaseEnv.projectId || !firebaseEnv.messagingSenderId) {
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        console.warn(
          '[Firebase] Configuracion incompleta. Define todas las credenciales en environment.'
        );
      }
      return null;
    }

    const config: FirebaseRuntimeConfig = {
      options: {
        apiKey: firebaseEnv.apiKey,
        appId: firebaseEnv.appId,
        projectId: firebaseEnv.projectId,
        messagingSenderId: firebaseEnv.messagingSenderId,
        authDomain: firebaseEnv.authDomain,
        storageBucket: firebaseEnv.storageBucket,
        measurementId: firebaseEnv.measurementId,
      },
      messaging: firebaseEnv.vapidKey ? {
        vapidKey: firebaseEnv.vapidKey
      } : undefined,
    };

    return config;
  }

  private async ensureAuthInitialized(app: FirebaseApp): Promise<void> {
    try {
      const auth = getAuth(app);
      // If the SDK provides authStateReady (v10+), prefer it
      const anyAuth: any = auth as any;
      if (typeof anyAuth.authStateReady === 'function') {
        await anyAuth.authStateReady();
        return;
      }
      // Fallback: wait for first auth state event
      await new Promise<void>((resolve) => {
        const unsub = onAuthStateChanged(auth, () => {
          unsub();
          resolve();
        });
      });
    } catch {
      // If auth fails to init, continue; other services may still work.
    }
  }
}
